<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <SevaTopbar
        app-name="OnePlaceHQ"
        product-name="Seva"
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        search-placeholder="Search patrons, donations..."
        @select-patron="handleSelectPatron"
        @select-donation="handleSelectDonation"
        @notifications="handleNotifications"
        @settings="handleSettings"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="dashboard-view">
      <!-- License Error Banner -->
      <div v-if="licenseError" class="license-error-banner">
        <div class="license-error-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h2>{{ licenseError.title }}</h2>
        <p>{{ licenseError.message }}</p>
        <IntercomButton variant="secondary" @click="handleLogout">
          Logout
        </IntercomButton>
      </div>

      <!-- Normal Dashboard Content -->
      <template v-else>
        <!-- Page Header -->
        <div class="page-header">
          <div>
            <h1 class="page-title">{{ $t('dashboard.title') }}</h1>
            <p class="page-subtitle">{{ $t('dashboard.subtitle') }}</p>
          </div>
          <IntercomButton variant="primary" @click="$router.push('/donations?new=true')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>{{ $t('donation.addDonation') }}</span>
          </IntercomButton>
        </div>

        <!-- Stats Grid -->
        <div class="stats-grid">
          <IntercomCard class="stat-card success">
            <div class="stat-label">{{ $t('dashboard.monthDonations') }}</div>
            <div class="stat-value" :title="formatFullCurrency(stats.this_month_donations || stats.month_donations || 0)">{{ formatCompactCurrency(stats.this_month_donations || stats.month_donations || 0) }}</div>
          </IntercomCard>

          <IntercomCard class="stat-card warning">
            <div class="stat-label">{{ $t('dashboard.monthExpenses') }}</div>
            <div class="stat-value" :title="formatFullCurrency(stats.this_month_expenses || 0)">{{ formatCompactCurrency(stats.this_month_expenses || 0) }}</div>
          </IntercomCard>

          <IntercomCard class="stat-card info">
            <div class="stat-label">{{ $t('dashboard.totalPatrons') }}</div>
            <div class="stat-value">{{ stats.total_patrons || 0 }}</div>
          </IntercomCard>

          <IntercomCard class="stat-card">
            <div class="stat-label">{{ $t('dashboard.todayActivity') }}</div>
            <div class="stat-value">{{ stats.today_meals || 0 }}</div>
          </IntercomCard>
        </div>

        <!-- Commitments Due -->
        <IntercomCard v-if="overdueCommitments.length > 0" class="due-card">
          <div class="due-header">
            <h3 class="section-title">{{ $t('dashboard.upcomingRenewals') }}</h3>
            <router-link to="/commitments" class="view-all-link">{{ $t('dashboard.viewAll') }}</router-link>
          </div>
          <div
            v-for="item in overdueCommitments.slice(0, 5)"
            :key="item.commitment.id"
            class="due-item"
            @click="$router.push(`/patrons/${item.commitment.patron_id}`)"
          >
            <div class="due-info">
              <span class="due-patron">{{ item.patron_name }}</span>
              <span class="due-amount">{{ formatCompactCurrency(item.commitment.amount) }}/{{ item.commitment.frequency }}</span>
            </div>
            <IntercomBadge :variant="item.days_until_due < 0 ? 'danger' : 'warning'">
              {{ item.days_until_due < 0 ? Math.abs(item.days_until_due) + 'd overdue' : item.days_until_due === 0 ? 'Today' : item.days_until_due + 'd' }}
            </IntercomBadge>
          </div>
        </IntercomCard>

        <!-- Account Balances -->
        <div v-if="accountBalances.length > 0" class="account-balances-section">
          <div class="section-header">
            <h3>Account Balances</h3>
            <IntercomButton variant="ghost" size="sm" @click="$router.push('/bank-accounts')">
              Manage
            </IntercomButton>
          </div>
          <div class="balance-cards">
            <IntercomCard
              v-for="acct in accountBalances"
              :key="acct.account_id"
              class="balance-card"
              :class="'type-' + acct.account_type"
            >
              <div class="balance-name">{{ acct.account_name }}</div>
              <IntercomBadge :variant="getAccountTypeVariant(acct.account_type)" size="sm">
                {{ acct.account_type }}
              </IntercomBadge>
              <div class="balance-amount" :title="formatFullCurrency(acct.current_balance)">
                {{ formatCompactCurrency(acct.current_balance) }}
              </div>
            </IntercomCard>
          </div>
        </div>

        <!-- Recent Donations -->
        <IntercomCard class="recent-section">
          <div class="section-header">
            <h3>{{ $t('dashboard.recentDonations') }}</h3>
            <IntercomButton variant="ghost" size="sm" @click="$router.push('/donations')">
              {{ $t('dashboard.viewAll') }}
            </IntercomButton>
          </div>
          <IntercomTable
            :columns="donationColumns"
            :data="recentDonations"
            :loading="isLoading"
            hoverable
            clickable
            @row-click="handleDonationClick"
          >
            <template #cell-donation_type="{ value }">
              <IntercomBadge :variant="getDonationTypeVariant(value)">
                {{ value ? $t(`donation.types.${value}`) : '' }}
              </IntercomBadge>
            </template>
            <template #cell-amount="{ value }">
              <span :title="formatFullCurrency(value)">{{ formatCompactCurrency(value) }}</span>
            </template>
            <template #cell-donation_date="{ value }">
              {{ formatDate(value) }}
            </template>
          </IntercomTable>
          <p v-if="!isLoading && recentDonations.length === 0" class="empty-text">
            {{ $t('donation.noDonations') }}
          </p>
        </IntercomCard>

        <!-- Recent Expenses -->
        <IntercomCard class="recent-section">
          <div class="section-header">
            <h3>{{ $t('dashboard.recentExpenses') }}</h3>
            <IntercomButton variant="ghost" size="sm" @click="$router.push('/expenses')">
              {{ $t('dashboard.viewAll') }}
            </IntercomButton>
          </div>
          <IntercomTable
            :columns="expenseColumns"
            :data="recentExpenses"
            :loading="isLoading"
            hoverable
            clickable
            @row-click="handleExpenseClick"
          >
            <template #cell-category="{ row }">
              {{ row.category?.name || '' }}
            </template>
            <template #cell-amount="{ value }">
              <span :title="formatFullCurrency(value)">{{ formatCompactCurrency(value) }}</span>
            </template>
            <template #cell-expense_date="{ value }">
              {{ formatDate(value) }}
            </template>
          </IntercomTable>
          <p v-if="!isLoading && recentExpenses.length === 0" class="empty-text">
            {{ $t('expense.noExpenses') }}
          </p>
        </IntercomCard>

        <!-- Program Breakdown (shown when programs exist with activity) -->
        <div v-if="programBreakdown.length > 0" class="program-breakdown-section">
          <div class="section-header">
            <h3>{{ $t('dashboard.programBreakdown') }}</h3>
            <IntercomButton variant="ghost" size="sm" @click="$router.push('/activity-log')">
              {{ $t('dashboard.viewAll') }}
            </IntercomButton>
          </div>
          <div class="program-breakdown-grid">
            <IntercomCard
              v-for="item in programBreakdown"
              :key="item.program_id"
              class="program-breakdown-card"
            >
              <div class="breakdown-program-name">{{ item.program_name }}</div>
              <div class="breakdown-metric">
                <span class="breakdown-metric-value">{{ formatNumber(item.metric_value) }}</span>
                <span class="breakdown-metric-label"> {{ item.metric_label }}</span>
              </div>
              <div class="breakdown-expense">{{ formatCompactCurrency(item.total_expense) }}</div>
            </IntercomCard>
          </div>
        </div>

        <!-- Upcoming Renewals -->
        <IntercomCard v-if="upcomingRenewals.length > 0" class="recent-section renewals-dashboard-section">
          <div class="section-header">
            <h3>{{ $t('dashboard.upcomingRenewals') }}</h3>
            <IntercomButton variant="ghost" size="sm" @click="$router.push('/renewals')">
              {{ $t('dashboard.viewAll') }}
            </IntercomButton>
          </div>
          <!-- Overdue rows -->
          <div v-if="overdueRenewals.length > 0" class="renewals-group">
            <div class="renewals-group-label overdue-label">{{ $t('renewals.overdue') }}</div>
            <IntercomTable
              :columns="renewalColumns"
              :data="overdueRenewals"
              hoverable
              clickable
              @row-click="(row) => $router.push(`/patrons/${row.patron_id}`)"
            >
              <template #cell-expected_amount="{ value }">
                {{ value ? formatCompactCurrency(value) : '-' }}
              </template>
              <template #cell-next_due_date="{ value }">
                <span class="overdue-date-text">{{ formatDate(value) }}</span>
              </template>
              <template #cell-days_until_due="{ value }">
                <span class="overdue-badge-inline">{{ Math.abs(value) }}d overdue</span>
              </template>
            </IntercomTable>
          </div>
          <!-- Due soon rows -->
          <div v-if="dueRenewals.length > 0" class="renewals-group">
            <div v-if="overdueRenewals.length > 0" class="renewals-group-label">{{ $t('renewals.upcoming') }}</div>
            <IntercomTable
              :columns="renewalColumns"
              :data="dueRenewals"
              hoverable
              clickable
              @row-click="(row) => $router.push(`/patrons/${row.patron_id}`)"
            >
              <template #cell-expected_amount="{ value }">
                {{ value ? formatCompactCurrency(value) : '-' }}
              </template>
              <template #cell-next_due_date="{ value }">
                {{ formatDate(value) }}
              </template>
              <template #cell-days_until_due="{ value }">
                <span v-if="value === 0" class="due-today-inline">Today</span>
                <span v-else class="upcoming-days-inline">in {{ value }}d</span>
              </template>
            </IntercomTable>
          </div>
        </IntercomCard>

        <!-- Year-End Compliance -->
        <IntercomCard class="compliance-card">
          <div class="compliance-header">
            <h3 class="section-title">FY {{ currentFY }} Compliance</h3>
            <IntercomButton variant="secondary" size="sm" @click="downloadCAExport" :loading="isExportingCA">
              CA Export (ZIP)
            </IntercomButton>
          </div>

          <div v-if="complianceData?.data" class="compliance-content">
            <!-- 85% Gauge -->
            <div class="application-gauge">
              <div class="gauge-bar">
                <div class="gauge-fill" :style="{ width: Math.min(complianceData.application_percentage, 100) + '%' }" :class="complianceData.status === 'compliant' ? 'gauge-compliant' : 'gauge-shortfall'"></div>
                <div class="gauge-threshold" style="left: 85%"></div>
              </div>
              <div class="gauge-stats">
                <div class="gauge-stat">
                  <span class="gauge-label">Income (excl. corpus)</span>
                  <span class="gauge-value">{{ formatCurrency(complianceData.income_subject_to_application) }}</span>
                </div>
                <div class="gauge-stat">
                  <span class="gauge-label">Applied (expenses)</span>
                  <span class="gauge-value">{{ formatCurrency(complianceData.actual_application) }}</span>
                </div>
                <div class="gauge-stat">
                  <span class="gauge-label">Application %</span>
                  <span class="gauge-value" :class="complianceData.status === 'compliant' ? 'text-green' : 'text-red'">
                    {{ complianceData.application_percentage.toFixed(1) }}%
                  </span>
                </div>
                <div v-if="complianceData.shortfall > 0" class="gauge-stat">
                  <span class="gauge-label">Shortfall</span>
                  <span class="gauge-value text-red">{{ formatCurrency(complianceData.shortfall) }}</span>
                </div>
              </div>
            </div>

            <!-- Fund Breakdown -->
            <div class="fund-breakdown">
              <div class="fund-item">
                <span class="fund-label">Corpus</span>
                <span class="fund-value">{{ formatCurrency(complianceData.data.corpus_donations) }}</span>
              </div>
              <div class="fund-item">
                <span class="fund-label">General</span>
                <span class="fund-value">{{ formatCurrency(complianceData.data.general_donations) }}</span>
              </div>
              <div class="fund-item">
                <span class="fund-label">Specific</span>
                <span class="fund-value">{{ formatCurrency(complianceData.data.specific_donations) }}</span>
              </div>
              <div class="fund-item">
                <span class="fund-label">Total Expenses</span>
                <span class="fund-value">{{ formatCurrency(complianceData.data.total_expenses) }}</span>
              </div>
            </div>
          </div>
          <p v-else class="empty-text">Loading compliance data...</p>
        </IntercomCard>
      </template>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { useSeva } from '../composables/useSeva.js'
import { formatCompactCurrency, formatFullCurrency } from '@shared/utils/currency.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomBadge,
} from '@shared/components/intercom'
import SevaTopbar from '../components/SevaTopbar.vue'
import { useNavigation } from '../composables/useNavigation.js'
import dayjs from 'dayjs'

const { t } = useI18n()
const router = useRouter()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { getDashboard, listDonations, listExpenses, getApplicationTest, exportCAPackage, getUpcomingRenewals } = useSeva()

const { navSections } = useNavigation()

// State
const stats = ref({})
const recentDonations = ref([])
const recentExpenses = ref([])
const accountBalances = ref([])
const programBreakdown = ref([])
const isLoading = ref(false)
const licenseError = ref(null)
const complianceData = ref(null)
const isExportingCA = ref(false)
const upcomingRenewals = ref([])

// Renewals split (for legacy table widget)
const overdueRenewals = computed(() => upcomingRenewals.value.filter(r => r.days_until_due < 0))
const dueRenewals = computed(() => upcomingRenewals.value.filter(r => r.days_until_due >= 0))

// Commitments due widget — overdue + due within 7 days, unfulfilled, sorted by urgency
const overdueCommitments = computed(() =>
  upcomingRenewals.value
    .filter((r) => !r.is_fulfilled && r.days_until_due <= 7)
    .sort((a, b) => a.days_until_due - b.days_until_due)
)

const renewalColumns = [
  { key: 'patron_name', title: t('donation.patron'), width: '160px' },
  { key: 'patron_phone', title: t('patron.phone'), width: '110px' },
  { key: 'expected_amount', title: t('donation.amount'), width: '100px', align: 'right' },
  { key: 'description', title: t('donation.description'), width: '150px' },
  { key: 'next_due_date', title: 'Due', width: '110px' },
  { key: 'days_until_due', title: 'Status', width: '110px' },
]

// Current fiscal year (India: April–March, e.g. "2025-26")
const currentFY = computed(() => {
  const now = new Date()
  const month = now.getMonth() // 0-indexed; April = 3
  const year = now.getFullYear()
  const fyStart = month >= 3 ? year : year - 1
  return `${fyStart}-${String(fyStart + 1).slice(-2)}`
})

const formatCurrency = (val) => formatFullCurrency(val || 0)

const formatNumber = (num) => (num != null ? num.toLocaleString('en-IN') : '0')

// Format date helper
const formatDate = (date) => {
  if (!date) return ''
  return dayjs(date).format('DD MMM YYYY')
}

// Table columns
const donationColumns = [
  { key: 'patron_name', title: t('donation.patron'), width: '150px' },
  { key: 'donation_type', title: t('donation.type'), width: '100px' },
  { key: 'amount', title: t('donation.amount'), width: '100px', align: 'right' },
  { key: 'donation_date', title: t('donation.date'), width: '120px' },
]

const expenseColumns = [
  { key: 'category', title: t('expense.category'), width: '150px' },
  { key: 'vendor_name', title: t('expense.vendor'), width: '150px' },
  { key: 'amount', title: t('expense.amount'), width: '100px', align: 'right' },
  { key: 'expense_date', title: t('expense.date'), width: '120px' },
]

/**
 * Get donation type badge variant
 */
const getAccountTypeVariant = (type) => {
  switch (type) {
    case 'savings': case 'current': return 'info'
    case 'cash': return 'success'
    case 'fd': return 'warning'
    case 'fcra': return 'default'
    default: return 'default'
  }
}

const getDonationTypeVariant = (type) => {
  switch (type) {
    case 'money':
      return 'success'
    case 'in_kind':
      return 'info'
    case 'volunteer':
      return 'warning'
    default:
      return 'default'
  }
}

/**
 * Fetch dashboard data
 */
const fetchDashboard = async () => {
  isLoading.value = true
  licenseError.value = null

  try {
    const [dashboardData, donationsData, expensesData] = await Promise.all([
      getDashboard().catch((err) => {
        if (err.errorCode === 'NO_LICENSE' || err.isLicenseError) {
          licenseError.value = {
            title: 'No License',
            message: err.detail || 'No valid license for seva module. Please contact your administrator.',
          }
        }
        return {}
      }),
      listDonations({ limit: 5, sort_by: 'donation_date', sort_order: 'desc' }).catch(() => ({ data: [] })),
      listExpenses({ limit: 5, sort_by: 'expense_date', sort_order: 'desc' }).catch(() => ({ data: [] })),
    ])

    stats.value = dashboardData || {}
    accountBalances.value = dashboardData?.account_balances || []
    programBreakdown.value = dashboardData?.program_breakdown || []
    recentDonations.value = donationsData?.data || []
    recentExpenses.value = expensesData?.data || []
  } catch (error) {
    console.error('Failed to fetch dashboard:', error)
  } finally {
    isLoading.value = false
  }
}

const handleDonationClick = (donation) => {
  router.push(`/donations/${donation.id}`)
}

const handleExpenseClick = (expense) => {
  router.push(`/expenses/${expense.id}`)
}

const handleSelectPatron = (patron) => {
  router.push(`/patrons/${patron.id}`)
}

const handleSelectDonation = (donation) => {
  router.push(`/donations/${donation.id}`)
}

const handleNotifications = () => {
  console.log('Notifications')
}

const handleSettings = () => {
  router.push('/settings')
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout error:', error)
    router.push('/login')
  }
}

const downloadCAExport = async () => {
  isExportingCA.value = true
  try {
    const blob = await exportCAPackage(currentFY.value)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `ca-export-FY${currentFY.value}.zip`
    link.click()
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Failed to download CA export:', error)
  } finally {
    isExportingCA.value = false
  }
}

onMounted(async () => {
  fetchBusiness()
  fetchDashboard()
  try {
    const result = await getApplicationTest(currentFY.value)
    complianceData.value = result
  } catch (error) {
    console.error('Failed to fetch compliance data:', error)
  }
  try {
    const renewalsResult = await getUpcomingRenewals({ days_ahead: 7 })
    upcomingRenewals.value = renewalsResult?.renewals || []
  } catch (error) {
    console.error('Failed to fetch renewals:', error)
  }
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.page-header {
  margin-bottom: var(--space-8);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  letter-spacing: var(--letter-spacing-tight);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

/* License Error Banner */
.license-error-banner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
  padding: var(--space-10);
  background: var(--color-gray-50);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-gray-200);
}

.license-error-icon {
  color: var(--color-warning-500);
  margin-bottom: var(--space-6);
}

.license-error-banner h2 {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-3) 0;
}

.license-error-banner p {
  font-size: var(--font-size-base);
  color: var(--color-gray-600);
  margin: 0 0 var(--space-6) 0;
  max-width: 400px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-5);
  margin-bottom: var(--space-8);
}

.stat-card {
  padding: var(--space-5);
  text-align: center;
}

.stat-label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin-bottom: var(--space-2);
}

.stat-value {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

.stat-card.warning .stat-value {
  color: #b45309;
}

.stat-card.success .stat-value {
  color: #059669;
}

.stat-card.info .stat-value {
  color: #2563eb;
}

.recent-section {
  margin-bottom: var(--space-6);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-4);
}

.section-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: 600;
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-8);
}

@media (max-width: 768px) {

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-3);
  }

  .stat-value {
    font-size: var(--font-size-xl);
  }
}

/* Account Balances */
.account-balances-section {
  margin-bottom: var(--space-6);
}

.account-balances-section .section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-4);
}

.account-balances-section h3 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0;
}

.balance-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--space-4);
}

.balance-card {
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.balance-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
}

.balance-amount {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
}

@media (max-width: 480px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
  .balance-cards {
    grid-template-columns: 1fr;
  }
}

/* Program Breakdown */
.program-breakdown-section {
  margin-bottom: var(--space-6);
}

.program-breakdown-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-4);
}

.program-breakdown-card {
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.breakdown-program-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
}

.breakdown-metric {
  display: flex;
  align-items: baseline;
  gap: var(--space-1);
}

.breakdown-metric-value {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  font-variant-numeric: tabular-nums;
}

.breakdown-metric-label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

.breakdown-expense {
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
}

/* Upcoming Renewals (dashboard) */
.renewals-dashboard-section {
  border-left: 3px solid #f59e0b;
}

.renewals-group {
  margin-bottom: var(--space-4);
}

.renewals-group:last-child {
  margin-bottom: 0;
}

.renewals-group-label {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-gray-500);
  margin-bottom: var(--space-2);
}

.overdue-label {
  color: #dc2626;
}

.overdue-date-text {
  color: #dc2626;
  font-weight: var(--font-weight-medium);
}

.overdue-badge-inline {
  display: inline-block;
  background: #fee2e2;
  color: #dc2626;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  padding: 2px var(--space-2);
  border-radius: var(--radius-sm);
}

.due-today-inline {
  display: inline-block;
  background: #fef3c7;
  color: #d97706;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  padding: 2px var(--space-2);
  border-radius: var(--radius-sm);
}

.upcoming-days-inline {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

/* Year-End Compliance */
.compliance-card {
  padding: var(--space-5);
  margin-top: var(--space-6);
}

.compliance-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-4);
}

.section-title {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
}

.application-gauge {
  margin-bottom: var(--space-4);
}

.gauge-bar {
  height: 24px;
  background: var(--color-gray-100);
  border-radius: var(--radius-md);
  position: relative;
  overflow: hidden;
}

.gauge-fill {
  height: 100%;
  border-radius: var(--radius-md);
  transition: width 0.5s ease;
}

.gauge-compliant { background: #10B981; }
.gauge-shortfall { background: #EF4444; }

.gauge-threshold {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #000;
}

.gauge-threshold::after {
  content: '85%';
  position: absolute;
  top: -18px;
  left: -10px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
}

.gauge-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-3);
  margin-top: var(--space-3);
}

.gauge-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.gauge-label {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
}

.gauge-value {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
}

.text-green { color: #10B981; }
.text-red { color: #EF4444; }

.fund-breakdown {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-3);
  padding-top: var(--space-3);
  border-top: 1px solid var(--color-gray-100);
}

.fund-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.fund-label {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
}

.fund-value {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

/* Commitments Due widget */
.due-card {
  padding: var(--space-5);
  margin-bottom: var(--space-6);
}

.due-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-3);
}

.view-all-link {
  font-size: var(--font-size-sm);
  color: var(--color-blue);
  text-decoration: none;
}

.view-all-link:hover {
  text-decoration: underline;
}

.due-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--color-gray-100);
  cursor: pointer;
}

.due-item:last-child {
  border-bottom: none;
}

.due-item:hover {
  background: var(--color-gray-50);
  margin: 0 calc(-1 * var(--space-3));
  padding-left: var(--space-3);
  padding-right: var(--space-3);
  border-radius: var(--radius-md);
}

.due-info {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.due-patron {
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.due-amount {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin-left: var(--space-2);
}
</style>
